import { motion } from 'framer-motion'
import { getOptimizedImageUrl } from '../lib/utils'

export default function Hero({ content }) {
  const optimizedHeroUrl = getOptimizedImageUrl(content.hero_image, 1600, 75);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-16 w-full"
    >
      {/* Background gradient - no blur, pure CSS gradient is fast */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary-light/20 via-warm-light to-cream" />

      {/* Decorative colored blobs - Visible on all devices for a rich modern Aurora background effect */}
      <div className="absolute top-[-10%] right-[-10%] w-72 h-72 md:w-96 md:h-96 bg-primary/30 md:bg-primary/20 rounded-full blur-[80px] md:blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-10%] w-80 h-80 md:w-[30rem] md:h-[30rem] bg-secondary/30 md:bg-secondary/20 rounded-full blur-[80px] md:blur-[100px] pointer-events-none" />
      <div className="absolute top-[40%] left-[20%] w-48 h-48 bg-accent/20 rounded-full blur-[60px] pointer-events-none hidden md:block" />

      {/* Background image if set — no backdrop-blur (kills mobile perf) */}
      {content.hero_image && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${optimizedHeroUrl})` }}
        >
          <img 
            src={optimizedHeroUrl} 
            alt="" 
            className="hidden" 
            fetchPriority="high" 
          />
          <div className="absolute inset-0 bg-cream/75" />
        </div>
      )}

      {/* Content — use animate (not whileInView) since hero is always visible on load */}
      <div className="relative z-10 text-center px-6 max-w-3xl mx-auto w-full">


        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.15, ease: 'easeOut' }}
          className="font-rubik text-5xl md:text-7xl font-bold text-text leading-tight mb-6"
          style={{ willChange: 'transform, opacity' }}
        >
          {content.hero_title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.28, ease: 'easeOut' }}
          className="text-text-light text-xl md:text-2xl leading-relaxed mb-10"
          style={{ willChange: 'transform, opacity' }}
        >
          {content.hero_subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.4, ease: 'easeOut' }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
          style={{ willChange: 'transform, opacity' }}
        >
          <a
            href="#project"
            className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full text-lg font-medium transition-colors duration-200 hover:shadow-lg active:scale-95"
          >
            לסדנה הקרובה
          </a>
          <a
            href="#contact"
            className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 rounded-full text-lg font-medium transition-colors duration-200 active:scale-95"
          >
            צרי קשר
          </a>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="w-6 h-10 border-2 border-primary-light rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-1.5 bg-primary rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  )
}
